import { Component, inject, computed, OnInit, signal } from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { AuthService } from '../../../core/services/auth.service';
import { CommonModule } from '@angular/common';
import { finalize, forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { LogoutModal } from '../logout-modal/logout-modal';
import { OutsideClickDirective } from '../../../directives/outside-click.directive';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageConfirmModal } from '../language-confirm-modal/language-confirm-modal';
import { Router, RouterLink } from '@angular/router';
import { NotificationService } from '../../../core/services/notification.service';
import { Notification } from '../../../core/models/notification';
import { SocketService } from '../../../core/services/socket.service';
import { FormsModule } from '@angular/forms';
import { TransactionService } from '../../../core/services/transaction.service';
import { UserService } from '../../../core/services/user.service';
import { countSearchCharacters, normalizeSearchTerm, shouldPreventLeadingSpace } from '../../utils/search.util';
import { buildSearchNavigationQueryParams, SearchNavigationQueryParams } from '../../utils/search-navigation.util';
import { TranslationExtenderService } from '../../../core/services/translation-extender.service';

@Component({
  selector: 'app-header',
  imports: [CommonModule, LogoutModal, OutsideClickDirective, TranslateModule, LanguageConfirmModal, RouterLink, FormsModule],
  templateUrl: './header.html',
  styleUrl: './header.css',
})
export class Header implements OnInit {
  private readonly minHeaderSearchCharacters = 5;
  private sidebarService = inject(SidebarService);
  private authService = inject(AuthService);
  private toastr = inject(ToastrService);
  private translate = inject(TranslateService);
  private translationExtender = inject(TranslationExtenderService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private socketService = inject(SocketService);
  private transactionService = inject(TransactionService);
  private userService = inject(UserService);

  searchQuery = signal('');
  showDropdown = signal(false);
  showNotificationDropdown = signal(false);
  showAccountRequestDropdown = signal(false);
  expandNotificationsList = signal(false);
  expandAccountRequestsList = signal(false);
  notifications = this.notificationService.notifications;
  isLoadingNotifications = this.notificationService.isLoading;
  showLogoutModal = signal(false);
  showLangDropdown = signal(false);
  pendingLanguage = signal<any>(null);
  showLangConfirmModal = signal(false);
  isSearchOpen = signal(false);
  showSearchNotFoundModal = signal(false);
  query = '';

  unreadCount = computed(() =>
    this.bellNotifications().filter(n => !n.is_read).length
  );

  bellNotifications = computed(() =>
    this.notifications().filter(n =>
      !['account_request', 'user:account-requested', 'account_requested', 'profile_update', 'user:profile-updated', 'profile_updated'].includes(n.type)
    )
  );

  limitedBellNotifications = computed(() => 
    this.expandNotificationsList() ? this.bellNotifications() : this.bellNotifications().slice(0, 3)
  );

  accountRequests = computed(() => 
    this.notifications().filter(n => 
      ['account_request', 'user:account-requested', 'account_requested', 'profile_update', 'user:profile-updated', 'profile_updated'].includes(n.type)
    )
  );

  limitedAccountRequests = computed(() => 
    this.expandAccountRequestsList() ? this.accountRequests() : this.accountRequests().slice(0, 3)
  );

  unreadAccountRequestsCount = computed(() => 
    this.accountRequests().filter(n => !n.is_read).length
  );

  /**
   * Toggle the search bar
   * @returns 
   */
  toggleSearch() {
    if (this.isSearchOpen() && normalizeSearchTerm(this.searchQuery())) {
      this.handleSearch();
      return;
    }
    this.isSearchOpen.update(v => !v);
  }

  onSearchInputChange(value: string) {
    this.searchQuery.set(value);
  }

  preventLeadingSpace(event: Event) {
    if (shouldPreventLeadingSpace(event)) {
      event.preventDefault();
    }
  }

  /**
   * Handle the search
   * Transaction ID or Client ID patterns will be detected and redirected to the appropriate page, otherwise it defaults to client search
   * @returns 
   */
  handleSearch() {
    this.query = normalizeSearchTerm(this.searchQuery());
    if (!this.query) return;
    if (countSearchCharacters(this.query) < this.minHeaderSearchCharacters) {
      this.toastr.error(
        `Please enter at least ${this.minHeaderSearchCharacters} characters`,
        'Invalid Search'
      );
      return;
    }

    const searchValue = this.getCanonicalSearchValue(this.query);
    const normalizedQuery = this.normalizeComparableValue(searchValue);
    const looksLikeTransactionQuery = /^GB-(FX|FIN|FTR|FXS|EX|FXE|CEX)-/i.test(searchValue) || /^GB-/i.test(searchValue);
    const searchNavigationParams = buildSearchNavigationQueryParams(this.router.url);

    forkJoin({
      transactions: this.transactionService.getTransactionList(undefined, 1, 10, { search: searchValue }),
      clients: this.userService.getUsers({ search: searchValue, limit: 10 }),
    }).subscribe({
      next: ({ transactions, clients }) => {
        if (transactions == null && clients == null) {
          this.showSearchNotFoundModal.set(true);
          this.searchQuery.set('');
          return;
        }

        const transactionDocs = this.extractDocs(transactions);
        const clientDocs = this.extractDocs(clients);

        const transactionMatch = transactionDocs.find((transaction: any) =>
          this.normalizeComparableValue(transaction?.transaction_id) === normalizedQuery ||
          this.normalizeComparableValue(transaction?.transaction_hash) === normalizedQuery
        );

        if (transactionMatch?._id) {
          this.router.navigate(['/transactions', transactionMatch._id], {
            queryParams: {
              ...searchNavigationParams,
              originSection: 'transactions',
            },
          });
          this.searchQuery.set('');
          return;
        }

        const clientMatch = clientDocs.find((client: any) =>
          this.normalizeComparableValue(client?.client_id) === normalizedQuery ||
          this.getClientFullName(client) === normalizedQuery
        );

        if (clientMatch?._id) {
          this.navigateToClientMainTab(clientMatch._id, searchNavigationParams);
          this.searchQuery.set('');
          return;
        }

        const hasTransactionListMatch = transactionDocs.some((transaction: any) =>
          this.containsComparableValue(transaction?.transaction_id, normalizedQuery) ||
          this.containsComparableValue(transaction?.transaction_hash, normalizedQuery)
        );

        const hasClientListMatch = clientDocs.some((client: any) =>
          this.containsComparableValue(client?.client_id, normalizedQuery) ||
          this.containsComparableValue(`${client?.first_name || ''} ${client?.last_name || ''}`, normalizedQuery)
        );

        if (hasTransactionListMatch && (looksLikeTransactionQuery || !hasClientListMatch)) {
          this.router.navigate(['/transactions'], {
            queryParams: {
              search: searchValue,
              ...searchNavigationParams,
              originSection: 'transactions',
            }
          });
          this.searchQuery.set('');
          return;
        }

        if (hasClientListMatch) {
          this.router.navigate(['/clients'], {
            queryParams: {
              search: searchValue,
              ...searchNavigationParams,
              originSection: 'clients',
            }
          });
          this.searchQuery.set('');
          return;
        }

        this.showSearchNotFoundModal.set(true);
        this.searchQuery.set('');
      },
      error: () => {
        this.toastr.error('Unable to complete search right now');
      },
    });
  }

  private navigateToClientMainTab(clientId: string, navigationQueryParams: SearchNavigationQueryParams) {
    this.router.navigate(['/clients', clientId], {
      queryParams: {
        tab: 'main',
        ...navigationQueryParams,
        originSection: 'clients',
      }
    });
  }

  private getClientFullName(client: any): string {
    return this.normalizeComparableValue(`${client?.first_name || ''} ${client?.last_name || ''}`);
  }

  private extractDocs(response: any): any[] {
    const payload = response?.data ?? response;
    const docs = payload?.docs ?? payload?.data ?? response?.docs ?? response?.data ?? response;
    return Array.isArray(docs) ? docs : [];
  }

  private normalizeComparableValue(value: string | null | undefined): string {
    return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private containsComparableValue(value: string | null | undefined, query: string): boolean {
    return this.normalizeComparableValue(value).includes(query);
  }

  private getCanonicalSearchValue(value: string): string {
    return /^GB-/i.test(value) ? value.toUpperCase() : value;
  }

  languages = [
    { name: 'English', code: 'EN', flag: '/assets/images/english.svg' },
    { name: 'Spanish', code: 'ES', flag: '/assets/images/spain.svg' },
    { name: 'French', code: 'FR', flag: '/assets/images/french.svg' }
  ];
  selectedLanguage = this.languages[0];

  ngOnInit() {
    // Listen for language changes from any part of the app
    this.translate.onLangChange.subscribe((event) => {
      const found = this.languages.find(l => l.code.toLowerCase() === event.lang.toLowerCase());
      if (found) {
        this.selectedLanguage = found;
      }
    });

    // Initial language setup
    const currentLang = this.translate.currentLang || localStorage.getItem('last_updated_language') || 'en';
    const found = this.languages.find(l => l.code.toLowerCase() === currentLang.toLowerCase());
    if (found) {
      this.selectedLanguage = found;
    }

    // Initial fetch of notifications on page load
    this.notificationService.refresh();

    // Listen for manual refreshes from other components (like NotificationsPage)
    this.notificationService.refreshNotifications$.subscribe(() => {
      this.notificationService.refresh();
    });

    // Listen for real-time notifications from WebSocket
    this.socketService.notifications$.subscribe((wsEvent) => {
      console.log('Real-time notification signal arrived:', wsEvent);
      
      // Trigger a global refresh signal for all components (Header + Notifications Page)
      this.notificationService.refreshNotifications$.next();
    });
  }

  /**
   * Toggle the language dropdown
   */
  toggleLangDropdown() {
    this.showLangDropdown.update(v => !v);
  }


  /**
   * Select a language
   * @param language 
   * @returns 
   */
  selectLanguage(language: any) {
    if (this.selectedLanguage.code.toLowerCase() === language.code.toLowerCase()) {
      this.showLangDropdown.set(false);
      return;
    }
    this.pendingLanguage.set(language);
    this.showLangConfirmModal.set(true);
    this.showLangDropdown.set(false);
  }

  /**
   * Confirm the language switch and apply the new language
   * @returns 
   */
  confirmLanguageSwitch() {
    const pending = this.pendingLanguage();
    if (!pending) return;

    const lang = pending.code.toLowerCase();
    this.selectedLanguage = pending;
    this.translate.use(lang);
    this.translationExtender.load(lang).subscribe();
    localStorage.setItem('last_updated_language', lang);

    this.showLangConfirmModal.set(false);
    this.pendingLanguage.set(null);
  }

  /**
   * Cancel the language switch and close the confirmation modal
   * @returns
   */
  cancelLanguageSwitch() {
    this.showLangConfirmModal.set(false);
    this.pendingLanguage.set(null);
  }

  // Expose member signal
  member = this.authService.member;

  // Compute initials from the member's name
  initials = computed(() => {
    const m = this.member();
    if (m?.first_name && m?.last_name) {
      return (m.first_name[0] + m.last_name[0]).toUpperCase();
    }
    if (m?.first_name) {
      return m.first_name[0].toUpperCase();
    }
    return 'U';
  });

  /**
   * Toggle the responsive sidebar visibility
   */
  toggleSidebar() {
    this.sidebarService.toggle();
  }

  /**
   * Toggle the user profile dropdown
   */
  toggleDropdown() {
    this.showDropdown.update(v => !v);
    if (this.showDropdown()) {
      this.showNotificationDropdown.set(false);
      this.showAccountRequestDropdown.set(false);
    }
  }

  /**
   * Toggle the notification dropdown and fetch notifications
   */
  toggleNotificationDropdown() {
    this.showNotificationDropdown.update(v => !v);
    if (this.showNotificationDropdown()) {
      this.showDropdown.set(false);
      this.showAccountRequestDropdown.set(false);
    } else {
        // Reset expansion when closing
        this.expandNotificationsList.set(false);
    }
  }

  /**
   * Toggle the account request dropdown
   */
  toggleAccountRequestDropdown() {
    this.showAccountRequestDropdown.update(v => !v);
    if (this.showAccountRequestDropdown()) {
      this.showDropdown.set(false);
      this.showNotificationDropdown.set(false);
    } else {
        // Reset expansion when closing
        this.expandAccountRequestsList.set(false);
    }
  }

  /**
   * Fetch current notifications for the admin
   */
  fetchNotifications() {
    this.notificationService.refresh();
  }

  /**
   * Mark a notification as read and handle redirection if it's an account request
   * @param notification 
   */
  markAsRead(notification: Notification) {
    if (notification.is_read) {
        this.handleRedirection(notification);
        return;
    }
    
    this.notificationService.markAsRead(notification._id).subscribe({
      next: () => {
        this.notificationService.notifications.update(list => 
            list.map(n => n._id === notification._id ? { ...n, is_read: true } : n)
        );
        this.handleRedirection(notification);
      },
      error: (error) => {
        console.error('Error marking notification as read:', error);
        // Still redirect if it fails? Probably better to just redirect if metadata is there
        this.handleRedirection(notification);
      }
    });
  }

  /**
   * Handle redirection based on notification metadata
   * @param notification 
   */
  private handleRedirection(notification: Notification) {
    console.log('Handling redirection for:', notification);
    const reminderRoute = this.getReminderRoute(notification);
    if (reminderRoute) {
      this.showAccountRequestDropdown.set(false);
      this.showNotificationDropdown.set(false);
      this.router.navigateByUrl(reminderRoute);
      return;
    }

    let redirectUrl = notification.meta_data?.redirect_url;
    
    if (redirectUrl) {
      this.showAccountRequestDropdown.set(false);
      this.showNotificationDropdown.set(false);
      
      try {
        // Handle absolute URLs by stripping the origin if it matches the current app
        if (redirectUrl.startsWith('http')) {
          // Fix potential common backend typos like http://http://
          const sanitizedUrl = redirectUrl.replace(/^http:\/\/http:\/\//, 'http://');
          const url = new URL(sanitizedUrl);
          
          // Internal navigation: use router to avoid page reload
          const internalPath = url.pathname + url.search + url.hash;
          this.router.navigateByUrl(internalPath);
        } else {
          // Relative path: use router directly
          this.router.navigateByUrl(redirectUrl);
        }
      } catch (e) {
        console.error('Redirection error:', e);
        // Fallback for malformed URLs that might still be relative paths
        if (redirectUrl) {
          this.router.navigateByUrl(redirectUrl);
        }
      }
    }
  }

  /**
   * Get the route for reminder notifications based on their metadata
   * @param notification 
   * @returns 
   */
  private getReminderRoute(notification: Notification): string | null {
    const type = (notification.type || '').toLowerCase();
    if (!type.includes('reminder')) {
      return null;
    }

    const meta = notification.meta_data;
    const redirectUrl = notification.meta_data?.redirect_url;
    const clientIdFromRedirect = this.extractClientIdFromUrl(redirectUrl);
    const clientId =
      clientIdFromRedirect ||
      meta?.['requested_id'] ||
      meta?.['user_id'] ||
      meta?.['client_id_value'] ||
      meta?.['client'];

    if (!clientId) {
      return null;
    }

    return `/clients/${clientId}?tab=reminders`;
  }

  /**
   * Extract client ID from a given URL, handling both absolute and relative URLs, and common backend formatting issues
   * @param urlValue
   */
  private extractClientIdFromUrl(urlValue?: string): string | null {
    if (!urlValue) {
      return null;
    }

    try {
      const url = urlValue.startsWith('http')
        ? new URL(urlValue.replace(/^http:\/\/http:\/\//, 'http://'))
        : new URL(urlValue, window.location.origin);

      const match = url.pathname.match(/\/clients\/([^/?#]+)/);
      return match?.[1] || null;
    } catch {
      const match = urlValue.match(/\/clients\/([^/?#]+)/);
      return match?.[1] || null;
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead() {
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notificationService.notifications.update(list => 
            list.map(n => ({ ...n, is_read: true }))
        );
      },
      error: (error) => {
        console.error('Error marking all as read:', error);
      }
    });
  }

  /**
   * Open the logout confirmation modal
   */
  openLogoutModal() {
    this.showDropdown.set(false);
    this.showLogoutModal.set(true);
  }

  /**
   * Close the logout confirmation modal
   */
  closeLogoutModal() {
    this.showLogoutModal.set(false);
  }

  /**
   * Confirm logout and execute the logout process
   */
  async confirmLogout() {
    try {
      await this.authService.logout();
      this.closeLogoutModal();
    } catch (error) {
      console.error('Logout error:', error);
      this.toastr.error(this.translate.instant('sessions.toasts.logout_failed'));
      this.closeLogoutModal();
    }
  }
}
